import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './utils/createBaseQuery';
import { IBroadcast } from '../../common/interfaces/Broadcast.interface';
import {
  IUpdateBroadcastUserStatusRequest,
  IGetBroadcastsRequest,
  IBroadcastsResponse,
} from '../../common/interfaces/BroadcastRequest.interface';
import { IBroadcastStats } from '../../common/interfaces/BroadcastStats.interface';

export const broadcastApi = createApi({
  reducerPath: 'broadcastApi',
  baseQuery: createBaseQuery('/broadcasts'),
  tagTypes: ['Broadcast', 'BroadcastUser', 'BroadcastStats'],
  endpoints: (builder) => ({
    // POST /broadcasts - Create new broadcast
    createBroadcast: builder.mutation<IBroadcast, FormData>({
      query: (formData) => ({
        url: '',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'Broadcast', id: 'LIST' }],
    }),

    // GET /broadcasts - Get all broadcasts with pagination and filters
    getBroadcasts: builder.query<
      IBroadcastsResponse,
      IGetBroadcastsRequest | void
    >({
      query: (params) => ({
        url: '',
        params: params || {},
      }),
      providesTags: (result) =>
        result?.data
          ? [
              { type: 'Broadcast', id: 'LIST' },
              ...result.data.map((broadcast: IBroadcast) => ({
                type: 'Broadcast' as const,
                id: broadcast.id,
              })),
            ]
          : [{ type: 'Broadcast', id: 'LIST' }],
    }),

    // GET /broadcasts/:id/stats - Get broadcast statistics
    getBroadcastStats: builder.query<IBroadcastStats, string>({
      query: (id) => ({
        url: `/${id}/stats`,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      }),
      transformResponse: (response: { code: number; data: IBroadcastStats }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: 'BroadcastStats', id }],
      keepUnusedDataFor: 0, // Don't cache stats data
    }),

    // PATCH /broadcasts/:broadcastId/users/:userId/status - Update broadcast user status
    updateBroadcastUserStatus: builder.mutation<
      void,
      {
        broadcastId: string;
        userId: string;
        data: IUpdateBroadcastUserStatusRequest;
      }
    >({
      query: ({ broadcastId, userId, data }) => ({
        url: `/${broadcastId}/users/${userId}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { broadcastId }) => [
        { type: 'BroadcastStats', id: broadcastId },
        { type: 'Broadcast', id: broadcastId },
      ],
    }),
  }),
});

export const {
  useCreateBroadcastMutation,
  useGetBroadcastsQuery,
  useLazyGetBroadcastsQuery,
  useGetBroadcastStatsQuery,
  useLazyGetBroadcastStatsQuery,
  useUpdateBroadcastUserStatusMutation,
} = broadcastApi;
